import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiDownload, FiFile, FiTrash2 } from 'react-icons/fi';
import {
  listDocuments,
  uploadDocument,
  deleteDocument,
  triggerDocumentDownload,
} from '../../api/documents';
import { extractErrorMessage } from '../../api/client';
import FileUploader from './FileUploader';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import Button from './Button';
import ConfirmDialog from './ConfirmDialog';
import { useAuth } from '../../auth/AuthContext';
import { formatNumber } from '../../utils/currency';

export const documentsQueryKey = (entityType, entityId) => [
  'documents',
  entityType,
  entityId,
];

export default function DocumentList({
  entityType,
  entityId,
  department,
  canUpload = true,
  canDelete,
}) {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const allowDelete = canDelete ?? isAdmin;

  const { data: docs, isLoading, error } = useQuery({
    queryKey: documentsQueryKey(entityType, entityId),
    queryFn: () => listDocuments(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });

  const upload = useMutation({
    mutationFn: (file) =>
      uploadDocument({ file, entityType, entityId, department }),
    onSuccess: () => {
      toast.success('Document uploaded');
      qc.invalidateQueries({ queryKey: documentsQueryKey(entityType, entityId) });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => deleteDocument(id),
    onSuccess: () => {
      toast.success('Document removed');
      qc.invalidateQueries({ queryKey: documentsQueryKey(entityType, entityId) });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const [pendingDelete, setPendingDelete] = useState(null);

  if (!entityId) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">Documents</h3>
          <p className="text-xs text-ink-muted">
            {docs?.length ? `${docs.length} file(s) attached` : 'No files yet'}
          </p>
        </div>
        {canUpload && (
          <FileUploader
            buttonLabel={upload.isPending ? 'Uploading...' : 'Upload'}
            disabled={upload.isPending}
            onSelect={(file) => upload.mutate(file)}
          />
        )}
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="px-5 py-4 text-sm text-danger">{extractErrorMessage(error)}</div>
      ) : !docs || docs.length === 0 ? (
        <EmptyState title="No documents" description="Attach receipts, invoices, or supporting files." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {docs.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded bg-primary/10 text-primary">
                  <FiFile />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">{doc.fileName}</div>
                  <div className="text-xs text-ink-muted">
                    {doc.fileType || 'file'} • {formatNumber(doc.fileSizeKb || 0)} KB
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  icon={FiDownload}
                  onClick={async () => {
                    try {
                      await triggerDocumentDownload(doc);
                    } catch (e) {
                      toast.error(extractErrorMessage(e));
                    }
                  }}
                >
                  Download
                </Button>
                {allowDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={FiTrash2}
                    onClick={() => setPendingDelete(doc)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete document?"
        message={`This will permanently remove "${pendingDelete?.fileName}".`}
        confirmLabel="Delete"
        variant="danger"
        loading={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          const id = pendingDelete.id;
          remove.mutate(id, { onSettled: () => setPendingDelete(null) });
        }}
      />
    </div>
  );
}
